import type { DependentRelation } from '../../../../core/fundPolicy/schema';
import type { Gender, IsoDate } from '../../../../core/primitives/schema';
import type { DependentStatus } from '../../domain/dependent/schema';

/**
 * The signed-in employee's own dependants — owned by this feature, implemented
 * in `infrastructure/` (§3.4, §4.1).
 *
 * Every method concerns the caller's own dependants. There is no "list by
 * employee id": the rules refuse it, and a port that offers the shape invites a
 * screen to try.
 */

export interface MyDependent {
  readonly id: string;
  readonly relation: DependentRelation;
  readonly fullName: string;
  readonly gender: Gender;
  readonly birthDate: IsoDate;
  readonly nationalId: string | null;
  readonly status: DependentStatus;
  readonly proofDocumentId: string | null;
}

/**
 * What an employee may submit.
 *
 * No `status` and no `employeeId`: an employee cannot mark their own dependant
 * active, and cannot claim one belongs to someone else. The server takes
 * ownership from the verified session (ADR-004).
 */
export interface NewDependent {
  readonly relation: DependentRelation;
  readonly fullName: string;
  readonly gender: Gender;
  readonly birthDate: IsoDate;
  readonly nationalId?: string;
}

export type DependentFailureCode =
  | 'forbidden'
  | 'submissions_closed'
  | 'not_editable'
  | 'network'
  | 'unknown';

export class DependentError extends Error {
  readonly code: DependentFailureCode;

  constructor(code: DependentFailureCode, message: string) {
    super(message);
    this.name = 'DependentError';
    this.code = code;
  }
}

export interface DependentPort {
  /**
   * The caller's own dependants.
   *
   * Bounded by the fund policy's own cap, so this cannot grow without someone
   * deliberately raising a limit (§11.5.1).
   */
  listMyDependents(): Promise<readonly MyDependent[]>;

  addDependent(dependent: NewDependent): Promise<void>;

  /** Only while still pending; the rules refuse edits after that. */
  updateDependent(id: string, dependent: NewDependent): Promise<void>;
}
